import {
  type GetClusterInferenceMetricsRequest,
  type GetClusterUsageMetricsRequest,
} from '@qdrant/qdrant-cloud-public-api/monitoring/v1';
import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { BookingServices } from '@/api/services/booking';
import { monitoringService } from '@/api/services/monitoring';
import { type DeepOmitProtobufInternals } from '@/connect-query';
import { isClusterHybridCloud } from '@/utils/cluster-utils';
import {
  findClusterPackage,
  getClusterPackageQueryOptions,
  isFreeTierBookingPackage,
} from '@/utils/package-tier-checks';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/_tabs/metrics',
)({
  // Observability surface — overrides the inherited `cluster` domain from the clusters subtree.
  staticData: {
    domain: 'monitoring',
  },
  beforeLoad: async ({ context: { queryClient, clusterQuery }, params: { accountId, clusterId } }) => {
    const { cluster } = await queryClient.ensureQueryData(clusterQuery);

    return {
      inferenceModelsQuery: isClusterHybridCloud(cluster)
        ? null
        : BookingServices.listInferenceModelsQuery({
            accountId,
            cloudProviderId: cluster.cloudProviderId,
            cloudProviderRegionId: cluster.cloudProviderRegionId,
          }),
      clusterUsageMetricsQuery: (query?: Pick<GetClusterUsageMetricsRequest, 'aggregator' | 'since' | 'until'>) =>
        monitoringService.getClusterUsageMetricsQuery({
          accountId,
          clusterId,
          ...query,
        }),
      getClusterInferenceMetricsQuery: (
        query: DeepOmitProtobufInternals<Omit<GetClusterInferenceMetricsRequest, 'accountId' | 'clusterId'>>,
      ) =>
        monitoringService.getClusterInferenceMetricsQuery({
          accountId,
          clusterId,
          ...query,
        }),
    };
  },
  loader: async ({ context: { queryClient, clusterQuery, inferenceModelsQuery } }) => {
    if (!inferenceModelsQuery) {
      return;
    }

    const { cluster } = await queryClient.ensureQueryData(clusterQuery);

    void queryClient
      .fetchQuery(BookingServices.listPackagesQuery(getClusterPackageQueryOptions(cluster)))
      .then(({ items }) =>
        isFreeTierBookingPackage(findClusterPackage(items, cluster))
          ? undefined
          : queryClient.prefetchQuery(inferenceModelsQuery),
      );
  },
  component: lazyRouteComponent(() => import('./metrics.component'), 'MetricsComponent'),
});
